import { usePostHog } from '@posthog/react';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { WalletCta } from '../../components/WalletCta';
import type { Endpoint } from '../../features/catalog/model';
import { cheapest } from '../../lib/catalog';
import { usdCost } from '../../lib/format';
import { signupPhrases, starterBalanceUsd } from '../../lib/signupOffer';
import { m } from '../../paraglide/messages';

export interface TryRunCtaProps {
  endpoint: Endpoint;
}

/**
 * Shown the moment a free public run succeeds, directly under the response.
 *
 * The endpoint pages are the best-engaged template on the site and the worst
 * converting: measured over the 90 days to 2026-08-17, 16.7% of sessions that
 * entered one ran this console and none of them created a key. A visitor who has
 * just watched real data come back has all the proof they are going to get, so
 * the ask belongs here rather than only in the nav and the page footer.
 *
 * It quotes the price of the endpoint they just ran, because "what does this
 * cost me in my own code" is the question a working result creates. The figure
 * comes from `cheapest`, the same serving-lane rule every other surface uses -
 * a price must not acquire a second source of truth. On a metered lane that
 * figure is the published ceiling, so the copy says "up to".
 */
export const TryRunCta = ({ endpoint }: TryRunCtaProps) => {
  const posthog = usePostHog();
  const price = cheapest(endpoint).price;

  return (
    <div className="rounded-12 border border-accent-line bg-accent-soft px-5 py-4 sm:flex sm:items-center sm:gap-5">
      <div className="min-w-0 flex-1">
        <p className="text-15 font-medium text-text">{m.endpoint_cta_title()}</p>
        <p className="mt-1 text-14 leading-relaxed text-text-2">
          <ParaglideMessage
            message={price.metered ? m.endpoint_cta_body_metered : m.endpoint_cta_body}
            inputs={{ price: usdCost(price.perRequestUsd), balance: starterBalanceUsd() }}
            markup={{ b: ({ children }) => <span className="font-semibold text-text">{children}</span> }}
          />
        </p>
      </div>
      <WalletCta
        label={signupPhrases().HUMAN_SIGNUP_OFFER_CTA}
        className="mt-3 w-full justify-center sm:mt-0 sm:w-auto sm:flex-none"
        onClick={() => posthog?.capture('try_run_cta_clicked', { sku: endpoint.sku })}
      />
    </div>
  );
};
